import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ModernizationAssessmentDetail {
  id: number;
  businessApplicationSysId: string;
  readinessScore: number | null;
  outcome: string | null;
  recommendedAction: string | null;
  keyReasons: string[] | null;
  requiresManualReview: boolean;
  missingRequiredFields: string[] | null;
  aiRecommendation: string | null;
  aiKeyReason: string | null;
  aiSettingsVersionUsed: number | null;
  aiJobId: number | null;
  aiAssessedAt: string | null;
  aiErrorMessage: string | null;
  isAiGenerated: boolean;
  cmdbConfidence: CmdbConfidenceSummary | null;
  assessmentBasis: string | null;
  preliminaryOutcome: string | null;
  lightweightValidationStatus: string | null;
  lightweightValidationSummary: string | null;
  lightweightValidationRecommendation: string | null;
  lightweightCodeAssessmentScore?: number | null;
  lightweightCodeAssessmentSignals?: LightweightAssessmentSignal[];
  lightweightCodeAssessmentScoreExplanation?: string | null;
  lightweightCodeAssessmentConfidence?: string | null;
  lightweightValidatedAt: string | null;
  isFinalized: boolean;
  finalizedAt: string | null;
  finalizedBy: string | null;
  businessCriticalityScore: number;
  lifeCycleScore: number;
  technicalPlatformScore: number;
  managementStyleScore: number;
  businessCriticality: string | null;
  lifeCycleStage: string | null;
  lifeCycleStageStatus: string | null;
  technicalPlatform: string | null;
  architectureTypes: string[] | null;
  applicationManagementStyle: string | null;
  createdAt: string;
  updatedAt: string;
  algorithmVersion: string | null;
  applicationName: string | null;
  applicationDescription: string | null;
  businessOwner: string | null;
  businessOwnerEmail: string | null;
  technicalOwner: string | null;
  technicalOwnerEmail: string | null;
  applicationLifeCycleStage: string | null;
  technologyStackJson: string | null;
  latestAiAssessment: any | null;
}

export interface LightweightAssessmentSignal {
  signal: string;
  evidence: string;
  severity: string;
  reason: string;
}

export interface CmdbConfidenceSignal {
  name: string;
  isPositive: boolean;
  detail: string;
}

export interface CmdbConfidenceSummary {
  score: number;
  level: string;
  signals: CmdbConfidenceSignal[];
}

export interface CmdbAnalysisRefreshJob {
  id: number;
  status: string | number;
  errorMessage?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ModernizationAssessmentService {
  private readonly baseUrl = `${environment.apiUrl}/assessments`;

  constructor(private http: HttpClient) {}

  /**
   * Get detailed modernization assessment by ID
   */
  getById(id: number): Observable<ModernizationAssessmentDetail> {
    return this.http.get<ModernizationAssessmentDetail>(`${this.baseUrl}/${id}`);
  }

  /**
   * Get AI assessment history for a modernization assessment
   */
  getAiAssessmentHistory(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/${id}/ai-history`);
  }

  /**
   * Refresh the AI-powered CMDB analysis for one application.
   */
  refreshCmdbAnalysis(sysId: string): Observable<{ success: boolean; message: string; jobId?: number }> {
    return this.http.post<{ success: boolean; message: string; jobId?: number }>(
      `${this.baseUrl}/cmdb-analysis/${encodeURIComponent(sysId)}/refresh`,
      {}
    );
  }

  getCmdbAnalysisRefreshJob(jobId: number): Observable<CmdbAnalysisRefreshJob> {
    return this.http.get<CmdbAnalysisRefreshJob>(
      `${environment.apiUrl}/admin/modernization-readiness/calculate/job/${jobId}`
    );
  }

  /**
   * Download the PDF report for a modernization assessment
   */
  downloadPdfReport(id: number, aiJobId?: number | null): Observable<Blob> {
    const url = aiJobId
      ? `${this.baseUrl}/${id}/report/pdf?aiJobId=${aiJobId}`
      : `${this.baseUrl}/${id}/report/pdf`;
    return this.http.get(url, { responseType: 'blob' });
  }
}
